/**
 * The de-rez wave. The scene renders to an RGBA target (MSAA on the full tier); a single
 * full-screen pass then writes the canvas, preserving alpha, and where a strike's ring is
 * passing it quantises the picture into character cells. A cell is classified by its true
 * coverage (16 taps): solids become shade blocks (░▒▓█) by coverage and brightness, things
 * thinner than a cell (cords, wires) become box strokes, empty space carries the ring as a
 * row of ░ at the front and a lattice of dots behind it. Colours snap to the palette.
 * The band has a crisp front and a tail that dissolves cell by cell; the strongest wave at
 * a cell wins, so overlapping rings stay rings. Outside the rings the scene passes straight
 * through (tone-mapped, sRGB, premultiplied).
 */
import {
  BufferAttribute, BufferGeometry, CanvasTexture, Color, GLSL3, HalfFloatType, LinearFilter,
  LinearSRGBColorSpace, Mesh, NoBlending, OrthographicCamera, Scene, ShaderMaterial, UnsignedByteType,
  Vector2, WebGLRenderTarget, type Camera, type TextureDataType, type WebGLRenderer,
} from 'three';
import { CONFIG, PALETTE } from '../config';
import type { Quality } from '../types';
import { GLYPH, GLYPH_SLOTS, buildGlyphAtlas } from './glyphs';

export const POST = {
  /** character cell in CSS px (scaled by DPR at runtime) */
  cellCss: { full: [8, 16], lite: [12, 24] } as Record<Quality['tier'], [number, number]>,
  /**
   * Band membership: a cell goes to text mode where the band value (strength × decay ×
   * profile, 0..1) beats threshold + dither × hash. The front row only needs the threshold,
   * so a ring's outline is always complete; behind it cells drop out as the profile falls.
   */
  threshold: 0.06,
  dither: 0.3,
  ditherHz: 9,
  /** leading-edge softness as a fraction of CONFIG.wave.widthPx: a crisp front */
  front: 0.08,
  /** tail profile exponent (< 1 keeps the band dense longer before it dissolves) */
  tailPow: 0.75,
  /** band width at zero strength as a fraction of widthPx (full width at strength 1) */
  minWidth: 0.55,
  /** a cell with less coverage than this is empty; a neighbour under it counts as empty */
  emptyCov: 0.12,
  /** a cell with less coverage than this may be a thin line (cord, wire) */
  thinCov: 0.85,
  /** display luminance that already earns a full block (bone sits just above it) */
  fullLum: 0.45,
  /** how far iron-snapped cells and stroke glyphs are pulled toward ink-dim so they read */
  ironLift: 0.35,
  strokeLift: 0.5,
  /** alpha of the ring over empty space: the ░ front row and the dot lattice behind it */
  dotAlpha: 0.5,
  exposure: 1.0,
  /** MSAA samples on the full tier */
  samples: 4,
  /** a wave older than this can never be visible on any screen */
  expireSec: CONFIG.wave.lifeSec * 3.5,
} as const;

const MAX = CONFIG.wave.maxActive;

const VERT = /* glsl */ `
void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
precision highp float;
precision highp int;

uniform sampler2D tScene;
uniform sampler2D uAtlas;
uniform vec2 uRes;
uniform vec2 uCell;
uniform float uTime;
uniform vec4 uWaves[MAX_WAVES];
uniform int uWaveCount;
uniform float uSpeed;
uniform float uWidth;
uniform float uLife;
uniform float uExposure;
uniform float uMotion;
uniform float uDotAlpha;
uniform float uThreshold;
uniform float uDither;
uniform float uFront;
uniform float uMinWidth;
uniform float uEmptyCov;
uniform float uThinCov;
uniform float uFullLum;
uniform float uIronLift;
uniform float uStrokeLift;
uniform vec3 uPalette[6];

out vec4 outColor;

float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

vec3 rrtOdt(vec3 v) {
  vec3 a = v * (v + 0.0245786) - 0.000090537;
  vec3 b = v * (0.983729 * v + 0.4329510) + 0.238081;
  return a / b;
}
vec3 aces(vec3 c) {
  const mat3 IN = mat3(0.59719, 0.07600, 0.02840, 0.35458, 0.90834, 0.13383, 0.04823, 0.01566, 0.83777);
  const mat3 OUT = mat3(1.60475, -0.10208, -0.00327, -0.53108, 1.10813, -0.07276, -0.07367, -0.00605, 1.07602);
  c *= uExposure / 0.6;
  c = OUT * rrtOdt(IN * c);
  return clamp(c, 0.0, 1.0);
}
vec3 toSRGB(vec3 c) {
  vec3 lo = c * 12.92;
  vec3 hi = 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055;
  return mix(lo, hi, step(vec3(0.0031308), c));
}
// premultiplied linear scene → premultiplied display colour
vec4 grade(vec4 s) {
  if (s.a < 1e-4) return vec4(0.0);
  vec3 c = toSRGB(aces(s.rgb / s.a));
  return vec4(c * s.a, s.a);
}
// The band at a cell: for every live wave, 1 at its front, falling to 0 one band width
// behind it, scaled by strength and age. The strongest wave wins (not the sum), so eight
// overlapping rings still read as rings. Dc is the signed distance to that wave's front in
// cell layers along the radial direction (a cell is wider than it is tall), so the front
// row is one cell thick all the way round.
void band(vec2 p, out float E, out float Dc) {
  E = 0.0; Dc = 1e9;
  for (int i = 0; i < MAX_WAVES; i++) {
    if (i >= uWaveCount) break;
    vec4 w = uWaves[i];
    float age = uTime - w.z;
    if (age < 0.0) continue;
    float k = w.w * exp(-age / uLife);
    vec2 r = p - w.xy;
    float len = max(length(r), 1e-3);
    float d = len - uSpeed * age; // > 0 ahead of the front
    float wd = uWidth * mix(uMinWidth, 1.0, k);
    float prof = d > 0.0
      ? 1.0 - smoothstep(0.0, uWidth * uFront, d)
      : pow(clamp(1.0 + d / wd, 0.0, 1.0), TAIL_POW);
    float e = k * prof;
    if (e > E) {
      E = e;
      vec2 n = abs(r) / len;
      Dc = d / (n.x * uCell.x + n.y * uCell.y);
    }
  }
}

// block density: ░ ▒ ▓ █ by coverage × brightness, never nothing (a cell in the band is a cell)
int shadeFor(float t) {
  if (t < 0.10) return G_SHADE1;
  if (t < 0.28) return G_SHADE2;
  if (t < 0.70) return G_SHADE3;
  return G_FULL;
}

float glyphMask(int glyph, vec2 fr) {
  return texture(uAtlas, vec2((float(glyph) + fr.x) / SLOTS, 1.0 - fr.y)).r;
}

// true coverage of a cell: 16 texel taps on a 4×4 grid (a 2 px cord is always counted).
// Also the four quadrant coverages, for telling a tilted line from an upright one.
vec4 cellCover(vec2 origin, out vec4 quad) {
  vec4 acc = vec4(0.0);
  quad = vec4(0.0); // TL, TR, BL, BR
  vec2 step = uCell * 0.25;
  ivec2 maxT = ivec2(uRes) - 1;
  for (int j = 0; j < 4; j++) {
    for (int i = 0; i < 4; i++) {
      vec2 p = origin + (vec2(float(i), float(j)) + 0.5) * step;
      vec4 s = texelFetch(tScene, clamp(ivec2(p), ivec2(0), maxT), 0);
      acc += s;
      bool top = j >= 2, right = i >= 2;
      if (top) { if (right) quad.y += s.a; else quad.x += s.a; }
      else     { if (right) quad.w += s.a; else quad.z += s.a; }
    }
  }
  quad *= 0.25;
  return acc * (1.0 / 16.0);
}
float cover(vec2 origin) { vec4 q; return cellCover(origin, q).a; }

void main() {
  vec2 fc = gl_FragCoord.xy;
  vec2 uv = fc / uRes;
  // cells are anchored top-left so the top row is whole
  vec2 pTop = vec2(fc.x, uRes.y - fc.y);
  vec2 cellId = floor(pTop / uCell);
  vec2 cellCTop = (cellId + 0.5) * uCell;
  vec2 cellC = vec2(cellCTop.x, uRes.y - cellCTop.y);

  float E = 0.0, Dc = 1e9;
  if (uWaveCount > 0) band(cellC, E, Dc);
  bool frontRow = Dc > -1.0 && E > uThreshold;
  float thr = uThreshold + uDither * hash12(cellId * 0.731 + floor(uTime * DITHER_HZ * uMotion) * 0.137 + 11.0);
  if (E < thr && !frontRow) { outColor = grade(texture(tScene, uv)); return; }

  vec2 fr = fract(pTop / uCell);
  vec2 origin = cellC - uCell * 0.5;
  vec4 quad;
  vec4 mean = cellCover(origin, quad);
  float a0 = mean.a;

  // nothing under this cell: the ring still passes, as a row of ░ at its front and a
  // lattice of dots behind it, dim enough to trace the logo rather than hide it
  if (a0 < uEmptyCov) {
    float m = glyphMask(frontRow ? G_SHADE1 : G_DOT, fr);
    float a = m * uDotAlpha * (frontRow ? clamp(E * 3.5, 0.4, 1.0) : clamp(E * 2.5, 0.0, 1.0));
    outColor = vec4(uPalette[0] * a, a);
    return;
  }

  vec4 gm = grade(mean);
  vec3 disp = gm.rgb / max(gm.a, 1e-4);
  float L = dot(disp, vec3(0.2126, 0.7152, 0.0722));

  int glyph = G_FULL;
  bool stroke = false;
  if (a0 < uThinCov) {
    // maybe a line thinner than a cell: look at the neighbours
    bool eL = cover(origin - vec2(uCell.x, 0.0)) < uEmptyCov;
    bool eR = cover(origin + vec2(uCell.x, 0.0)) < uEmptyCov;
    bool eU = cover(origin + vec2(0.0, uCell.y)) < uEmptyCov;
    bool eD = cover(origin - vec2(0.0, uCell.y)) < uEmptyCov;
    bool vert = eL && eR && !(eU && eD);
    bool horz = eU && eD && !(eL && eR);
    if (vert || horz) {
      stroke = true;
      float dg = (quad.x + quad.w) - (quad.y + quad.z); // TL+BR vs TR+BL
      if (abs(dg) > 0.5 * a0) glyph = dg > 0.0 ? G_BSLASH : G_SLASH;
      else glyph = vert ? G_VBAR : G_HBAR;
    }
  }
  if (!stroke) {
    // a solid, or the edge of one: block density from coverage and brightness
    float Ln = clamp(L / uFullLum, 0.0, 1.0);
    glyph = shadeFor(a0 * (0.3 + 0.7 * Ln));
  }
  float m = glyphMask(glyph, fr);

  vec3 pal = uPalette[0];
  float best = 1e9;
  int bi = 0;
  for (int i = 0; i < 6; i++) {
    float dd = distance(disp, uPalette[i]);
    if (dd < best) { best = dd; pal = uPalette[i]; bi = i; }
  }
  // iron is too close to the room to read as a cell; strokes are drawn lines, so lift both toward ink
  if (bi == 5) pal = mix(pal, uPalette[0], uIronLift);
  if (stroke) pal = mix(pal, uPalette[0], uStrokeLift);
  // the glyph already carries the coverage; only the faintest cells fade
  float a = m * smoothstep(uEmptyCov, uEmptyCov * 2.0, a0);
  outColor = vec4(pal * a, a);
}
`;

interface Wave { x: number; y: number; t0: number; strength: number }

export class DerezPost {
  private readonly renderer: WebGLRenderer;
  private readonly lite: boolean;
  private readonly material: ShaderMaterial;
  private readonly postScene = new Scene();
  private readonly postCam = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly atlas: CanvasTexture;
  private readonly tri: Mesh;
  private target: WebGLRenderTarget | null = null;
  private readonly waves: Wave[] = [];
  private readonly waveData = new Float32Array(MAX * 4);
  private cssH = 1;
  private dpr = 1;
  private readonly halfFloatOk: boolean;
  /** debug: waves run on their own clock so ?slow=N can stretch them for inspection */
  timeScale = 1;
  private clock = 0;
  private lastNow = -1;
  /** the pixel type the target ended up with (for the debug report) */
  targetType: 'half-float' | 'byte' = 'byte';

  constructor(renderer: WebGLRenderer, quality: Quality) {
    this.renderer = renderer;
    this.lite = quality.tier === 'lite';
    this.halfFloatOk = !this.lite && (renderer.extensions.has('EXT_color_buffer_float') || renderer.extensions.has('EXT_color_buffer_half_float'));

    this.atlas = new CanvasTexture(buildGlyphAtlas(8, 16));
    this.atlas.minFilter = LinearFilter;
    this.atlas.magFilter = LinearFilter;
    this.atlas.generateMipmaps = false;

    const defines: Record<string, string | number> = {
      MAX_WAVES: MAX,
      SLOTS: `${GLYPH_SLOTS}.0`,
      DITHER_HZ: `${POST.ditherHz}.0`,
      TAIL_POW: POST.tailPow.toFixed(3),
      G_SPACE: GLYPH.SPACE, G_SHADE1: GLYPH.SHADE1, G_SHADE2: GLYPH.SHADE2, G_SHADE3: GLYPH.SHADE3, G_FULL: GLYPH.FULL,
      G_VBAR: GLYPH.VBAR, G_HBAR: GLYPH.HBAR, G_SLASH: GLYPH.SLASH, G_BSLASH: GLYPH.BSLASH, G_CROSS: GLYPH.CROSS,
      G_TL: GLYPH.TL, G_TR: GLYPH.TR, G_BL: GLYPH.BL, G_BR: GLYPH.BR, G_DOT: GLYPH.DOT,
    };
    this.material = new ShaderMaterial({
      glslVersion: GLSL3,
      defines,
      uniforms: {
        tScene: { value: null },
        uAtlas: { value: this.atlas },
        uRes: { value: new Vector2(1, 1) },
        uCell: { value: new Vector2(0, 0) },
        uTime: { value: 0 },
        uWaves: { value: this.waveData },
        uWaveCount: { value: 0 },
        uSpeed: { value: CONFIG.wave.speedPxPerSec },
        uWidth: { value: CONFIG.wave.widthPx },
        uLife: { value: CONFIG.wave.lifeSec },
        uExposure: { value: POST.exposure },
        uMotion: { value: quality.reducedMotion ? 0 : 1 },
        uDotAlpha: { value: POST.dotAlpha },
        uThreshold: { value: POST.threshold },
        uDither: { value: POST.dither },
        uFront: { value: POST.front },
        uMinWidth: { value: POST.minWidth },
        uEmptyCov: { value: POST.emptyCov },
        uThinCov: { value: POST.thinCov },
        uFullLum: { value: POST.fullLum },
        uIronLift: { value: POST.ironLift },
        uStrokeLift: { value: POST.strokeLift },
        uPalette: { value: paletteDisplay() },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      depthTest: false,
      depthWrite: false,
      blending: NoBlending,
    });

    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    this.tri = new Mesh(geo, this.material);
    this.tri.frustumCulled = false;
    this.postScene.add(this.tri);
  }

  /** (re)build the scene target at the drawing-buffer size */
  resize(pxW: number, pxH: number, cssH: number, dpr: number) {
    this.cssH = cssH;
    this.dpr = dpr;
    if (this.target) { this.target.dispose(); this.target = null; }
    this.target = this.makeTarget(Math.max(1, pxW), Math.max(1, pxH));
    const u = this.material.uniforms;
    (u.uRes.value as Vector2).set(pxW, pxH);
    const cell = POST.cellCss[this.lite ? 'lite' : 'full'];
    const cw = Math.max(2, Math.round(cell[0] * dpr)), ch = Math.max(4, Math.round(cell[1] * dpr));
    const cv = u.uCell.value as Vector2;
    if (cv.x !== cw || cv.y !== ch) {
      cv.set(cw, ch);
      // redraw the atlas at the device cell size so texels land on pixels
      this.atlas.image = buildGlyphAtlas(cw, ch);
      this.atlas.needsUpdate = true;
    }
    // wave positions were stored in CSS px, so nothing to convert here
    u.uSpeed.value = CONFIG.wave.speedPxPerSec * dpr;
    u.uWidth.value = CONFIG.wave.widthPx * dpr;
    u.tScene.value = this.target.texture;
  }

  private makeTarget(w: number, h: number): WebGLRenderTarget {
    const types: TextureDataType[] = this.halfFloatOk ? [HalfFloatType, UnsignedByteType] : [UnsignedByteType];
    const samples = this.lite ? 0 : POST.samples;
    const gl = this.renderer.getContext();
    let last: WebGLRenderTarget | null = null;
    for (const type of types) {
      for (const s of samples > 0 ? [samples, 0] : [0]) {
        const rt = new WebGLRenderTarget(w, h, {
          type, samples: s, depthBuffer: true, stencilBuffer: false, generateMipmaps: false,
          minFilter: LinearFilter, magFilter: LinearFilter, colorSpace: LinearSRGBColorSpace,
        });
        this.renderer.setRenderTarget(rt);
        const ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE && gl.getError() === gl.NO_ERROR;
        this.renderer.setRenderTarget(null);
        if (ok) { this.targetType = type === HalfFloatType ? 'half-float' : 'byte'; return rt; }
        rt.dispose();
        last = rt;
      }
    }
    // nothing validated (should not happen on WebGL2): fall back to the plainest target
    void last;
    this.targetType = 'byte';
    return new WebGLRenderTarget(w, h, { type: UnsignedByteType, depthBuffer: true, stencilBuffer: false, generateMipmaps: false, minFilter: LinearFilter, magFilter: LinearFilter });
  }

  addWave(x: number, y: number, strength: number) {
    if (!(strength > 0)) return;
    const w: Wave = { x, y, t0: this.clock, strength: Math.min(1, strength) };
    if (this.waves.length >= MAX) {
      // replace the oldest
      let oi = 0;
      for (let i = 1; i < this.waves.length; i++) if (this.waves[i].t0 < this.waves[oi].t0) oi = i;
      this.waves[oi] = w;
    } else {
      this.waves.push(w);
    }
  }

  get activeWaves(): number { return this.waves.length; }

  private uploadWaves(now: number) {
    if (this.lastNow >= 0) this.clock += Math.max(0, Math.min(0.1, now - this.lastNow)) * this.timeScale;
    this.lastNow = now;
    now = this.clock;
    // expire
    for (let i = this.waves.length - 1; i >= 0; i--) {
      if (now - this.waves[i].t0 > POST.expireSec) { this.waves[i] = this.waves[this.waves.length - 1]; this.waves.pop(); }
    }
    const d = this.waveData;
    for (let i = 0; i < this.waves.length; i++) {
      const w = this.waves[i];
      d[i * 4] = w.x * this.dpr;
      d[i * 4 + 1] = (this.cssH - w.y) * this.dpr;
      d[i * 4 + 2] = w.t0;
      d[i * 4 + 3] = w.strength;
    }
    this.material.uniforms.uWaveCount.value = this.waves.length;
    this.material.uniforms.uTime.value = now;
  }

  render(scene: Scene, camera: Camera, now: number) {
    if (!this.target) return;
    this.uploadWaves(now);
    const r = this.renderer;
    r.setRenderTarget(this.target);
    r.render(scene, camera);
    r.setRenderTarget(null);
    r.render(this.postScene, this.postCam);
  }

  dispose() {
    this.target?.dispose();
    this.material.dispose();
    this.atlas.dispose();
    this.tri.geometry.dispose();
  }
}

/** the six snap colours as display (sRGB) triplets, exactly as CSS shows them */
function paletteDisplay(): Color[] {
  return [PALETTE.inkDim, PALETTE.bone, PALETTE.red, PALETTE.redHot, PALETTE.gold, PALETTE.iron].map((hex) => {
    const n = parseInt(hex.slice(1), 16);
    return new Color(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
  });
}
